import { createSlice } from '@reduxjs/toolkit';
import type { AxiosError, AxiosResponse } from 'axios';
import { api, payments } from 'libs';
import type { PaymentFormSubmitData } from 'components/subscriptions/types';
import initialState from 'state/initial-state';
import serializeRequestError from 'state/utils/serialize-request-error';
import { actionTypes as brokerages } from './brokerages';
import type { AppDispatch, AppGetState, RequestResultAction } from './index';

const usersSlice = createSlice({
  name: 'lenderspotlight/users',
  initialState: {
    currentUser: initialState.data?.user ?? null,
    isUploading: false,
  },
  reducers: {
    deletePaymentStarted: () => {},
    deletePaymentFailed: () => {},
    deletePaymentSucceeded: (state, action) => {
      state.currentUser = action.payload;
    },
    fetchStarted: () => {},
    fetchFailed: () => {},
    fetchMeSucceeded: (state, action) => {
      state.currentUser = action.payload;
    },
    preferredLendersUpdated: (state, action) => {
      state.currentUser = {
        ...state.currentUser,
        ...action.payload,
      };
    },
    updateStarted: () => {},
    updateFailed: () => {},
    updateSucceeded: (state, action) => {
      state.currentUser = action.payload;
    },
    updatePaymentStarted: () => {},
    updatePaymentFailed: () => {},
    updatePaymentSucceeded: () => {},
    uploadStarted: (state, action) => {
      const { previousPhotoUrl, previewUrl } = action.payload;
      state.currentUser = {
        ...state.currentUser,
        old_photo: previousPhotoUrl,
        photo_url: previewUrl,
      };
      state.isUploading = true;
    },
    uploadFailed: (state, action) => {
      const previousPhotoUrl = action.payload?.previousPhotoUrl ?? state.currentUser.old_photo;
      state.currentUser = {
        ...state.currentUser,
        photo_url: previousPhotoUrl,
      };
      state.isUploading = false;
    },
    uploadSucceeded: (state, action) => {
      const photoUrl = action.payload?.photoUrl;
      if (photoUrl) {
        state.currentUser = {
          ...state.currentUser,
          photo_url: photoUrl,
        };
      }
      state.isUploading = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(brokerages.updateSucceeded, (state, action) => {
      const brokerageId = state.currentUser?.brokerage?.id;
      const updatedAllowJoinRequests = brokerageId
        ? (action as any).payload.entities?.brokerages?.[brokerageId]?.setting_allow_join_requests
        : undefined;

      state.currentUser = {
        ...state.currentUser,
        brokerage: {
          ...state.currentUser.brokerage,
          name: (action as any).payload.brokerageName,
          setting_allow_join_requests:
            updatedAllowJoinRequests ?? state.currentUser.brokerage?.setting_allow_join_requests,
        },
      };
    });
  },
});

export default usersSlice.reducer;

export const { preferredLendersUpdated } = usersSlice.actions;

const {
  deletePaymentFailed,
  deletePaymentStarted,
  deletePaymentSucceeded,
  fetchFailed,
  fetchMeSucceeded,
  fetchStarted,
  updateFailed,
  updatePaymentFailed,
  updatePaymentStarted,
  updatePaymentSucceeded,
  updateStarted,
  updateSucceeded,
  uploadFailed,
  uploadStarted,
  uploadSucceeded,
} = usersSlice.actions;

/**
 * Action Types
 * @type {Object}
 */
export const actionTypes = {
  deletePaymentFailed: deletePaymentFailed.type,
  deletePaymentSucceeded: deletePaymentSucceeded.type,
  updateFailed: updateFailed.type,
  updateSucceeded: updateSucceeded.type,
  uploadSucceeded: uploadSucceeded.type,
};

/**
 * Fetch Users Failed Response Action
 * @param {Error} error
 * @return {Object}
 */
const fetchRequestFailed = (error: unknown) => ({ type: fetchFailed.type, error: serializeRequestError(error) });

/**
 * Update User Failed Reponse Action
 * @param {Error} error
 * @return {Object}
 */
const updateRequestFailed = (error: unknown) => {
  const payload = (error as AxiosError<{ errors?: unknown }>).response?.data?.errors ?? {};
  return {
    type: updateFailed.type,
    error: serializeRequestError(error),
    payload,
  };
};

/**
 * Update User Success Action
 * @param {Object} response
 * @return {Object}
 */
const updateSuccess = (response: AxiosResponse) => updateSucceeded(response.data);

/**
 * Fetch Current User Thunk
 * @return {Promise}
 */
export const fetchMe = () => async (dispatch: AppDispatch) => {
  dispatch(fetchStarted());

  try {
    const response = await api.get('me');
    return dispatch(fetchMeSucceeded(response.data));
  } catch (error) {
    return dispatch(fetchRequestFailed(error));
  }
};

/**
 * Mark Current User As Onboarded Thunk
 * @return {Promise}
 */
export const markAsOnboarded = () => async (dispatch: AppDispatch) => {
  dispatch(updateStarted());

  try {
    const response = await api.put('me/onboarding');
    return dispatch(updateSuccess(response));
  } catch (error) {
    return dispatch(updateRequestFailed(error));
  }
};

export const clearNotification = (pathname: string) => async (dispatch: AppDispatch) => {
  dispatch(updateStarted());

  try {
    const response = await api.delete('user-notifications', { data: { pathname } });
    return dispatch(updateSuccess(response));
  } catch (error) {
    return dispatch(updateRequestFailed(error));
  }
};

export const cancelPendingEmail = (userId: string | number) => async (dispatch: AppDispatch) => {
  dispatch(updateStarted());

  try {
    const response = await api.delete(`users/${userId}/pending-email`);
    return dispatch(updateSuccess(response));
  } catch (error) {
    return dispatch(updateRequestFailed(error));
  }
};

/**
 * Update User Thunk
 * @param {Object} user
 * @return {Function}
 */
export const update =
  (userId: string | number, data: Record<string, unknown>) =>
  async (dispatch: AppDispatch): Promise<RequestResultAction> => {
    dispatch(updateStarted());

    try {
      const response = await api.put(`users/${userId}`, data);
      return dispatch(updateSuccess(response));
    } catch (error) {
      return dispatch(updateRequestFailed(error));
    }
  };

/**
 * User Answered Questionnaire Thunk
 * @param {Object} pass
 * @return {Function}
 */
export const answerQuestionnaire =
  (userId: string | number, data: Record<string, unknown>) => async (dispatch: AppDispatch) => {
    dispatch(updateStarted());

    try {
      const response = await api.put(`users/${userId}/questionnaire`, data);
      return dispatch(updateSuccess(response));
    } catch (error) {
      return dispatch(updateRequestFailed(error));
    }
  };

/**
 * Update User Payment Card Thunk
 * @param {Object} card
 * @return {Function}
 */
export const updatePaymentCard =
  (userId: string | number, data: PaymentFormSubmitData) =>
  async (dispatch: AppDispatch): Promise<RequestResultAction> => {
    dispatch(updatePaymentStarted());

    const card = {
      ...(data.setupIntent ? payments.parseStripeToken(data.setupIntent) : null),
      pm_postal: data.pm_postal,
    };

    try {
      const response = await api.put(`users/${userId}/payment-card`, card);
      return dispatch(updateSuccess(response));
    } catch (error) {
      return dispatch(updateRequestFailed(error));
    }
  };

export const deletePaymentCard = (userId: string | number) => async (dispatch: AppDispatch) => {
  dispatch(deletePaymentStarted());

  try {
    const response = await api.delete(`users/${userId}/payment-card`);
    return dispatch(deletePaymentSucceeded(response.data));
  } catch (error) {
    return dispatch({ type: deletePaymentFailed.type, error: serializeRequestError(error) });
  }
};

/**
 * Upload User Avatar Thnuk.
 * @param {Object} photo
 * @return {Function}
 */
export const uploadPhoto =
  (userId: string | number, photo: File) => async (dispatch: AppDispatch, getState: AppGetState) => {
    const previousPhotoUrl = getState().users.currentUser?.photo_url ?? null;
    const data = new FormData();
    data.append('avatar', photo);

    const readPreview = () =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target?.result ?? null);
        reader.onerror = () => reject(new Error('Failed to read avatar preview'));
        reader.readAsDataURL(photo);
      });

    try {
      const previewUrl = await readPreview();
      dispatch(uploadStarted({ previewUrl, previousPhotoUrl }));

      try {
        const response = await api.post(`users/${userId}/avatar`, data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        return dispatch(
          uploadSucceeded({
            photoUrl: response.data?.photo_url ?? response.data?.data?.photo_url ?? previousPhotoUrl,
          })
        );
      } catch (error) {
        return dispatch({
          type: uploadFailed.type,
          error: serializeRequestError(error),
          payload: { previousPhotoUrl },
        });
      }
    } catch (error) {
      return dispatch({
        type: uploadFailed.type,
        error: serializeRequestError(error),
        payload: { previousPhotoUrl },
      });
    }
  };
